import { useEffect, useState, useCallback } from "react";
import {
  Zap, Filter, Bell, Play, Clock, GitBranch, Sparkles,
  ShoppingCart, Users, BarChart3, Calendar, Wallet, Briefcase, Building2,
  Search, HelpCircle, Plus, Copy, ToggleRight, ToggleLeft, Trash2,
} from "lucide-react";
import FlowTutorialDialog from "@/components/FlowTutorialDialog";
import { FlowErrorBoundary } from "@/components/FlowErrorBoundary";
import { NewFlowWizard } from "./flows/NewFlowWizard";
import { FlowCanvas } from "./flows/FlowCanvas";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1/flows`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("session_token")}` });

const CATEGORY_META = {
  "Sales":         { color: "#8B1A1A", bg: "#FAEDED", icon: ShoppingCart, blurb: "Orders, dealers, schemes" },
  "Field Force":   { color: "#0EA5E9", bg: "#E0F2FE", icon: Users,        blurb: "Attendance, GPS, visits" },
  "Operations":    { color: "#22C55E", bg: "#EAF3DE", icon: Building2,    blurb: "Dispatch, delivery" },
  "Finance":       { color: "#F59E0B", bg: "#FAEEDA", icon: Wallet,       blurb: "Collections, outstanding" },
  "HR":            { color: "#8B5CF6", bg: "#EEEDFE", icon: Briefcase,    blurb: "Leaves, expenses, grievances" },
  "Intelligence":  { color: "#EC4899", bg: "#FCE7F3", icon: BarChart3,    blurb: "Targets, leaderboard" },
  "Scheduled":     { color: "#6B7280", bg: "#F1EFE8", icon: Calendar,     blurb: "Daily, weekly, monthly" },
};

// Categories + their sub-categories used by the custom-trigger form.
// Keep in sync with TRIGGER_CATALOGUE on the backend.
const CT_CATEGORIES = ["Sales", "Field Force", "Operations", "Finance", "HR", "Intelligence", "Scheduled", "Custom"];
const CT_SUBCATEGORIES = {
  "Sales":         ["Orders", "Dealers", "Schemes", "Other"],
  "Field Force":   ["Attendance", "GPS", "Visits", "Other"],
  "Operations":    ["Dispatch", "Delivery", "Other"],
  "Finance":       ["Collections", "Outstanding", "Other"],
  "HR":            ["Leaves", "Expenses", "Grievances", "Other"],
  "Intelligence":  ["Targets", "Leaderboard", "Other"],
  "Scheduled":     ["Daily", "Weekly", "Monthly", "Other"],
  "Custom":        ["General", "Webhook", "Integration", "Other"],
};

const NODE_STYLE = {
  TRIGGER:   { bg: "#E6F1FB", border: "#3B82F6", icon: Zap,       label: "Trigger" },
  CONDITION: { bg: "#FAEEDA", border: "#F59E0B", icon: Filter,    label: "Condition" },
  ACTION:    { bg: "#EAF3DE", border: "#22C55E", icon: Play,      label: "Action" },
  NOTIFY:    { bg: "#EEEDFE", border: "#8B5CF6", icon: Bell,      label: "Notify" },
  WAIT:      { bg: "#F1EFE8", border: "#6B7280", icon: Clock,     label: "Wait" },
  BRANCH:    { bg: "#FCE7F3", border: "#EC4899", icon: GitBranch, label: "Branch" },
};


// NOTE: FlowNode, fromFlowDoc/toFlowDoc, nodeTypes, palette, FlowCanvas, NodeConfigPanel,
// BranchEditor moved to ./flows/FlowCanvas.jsx (iter83 refactor).
// ChipInput + NewFlowWizard live in ./flows/NewFlowWizard.jsx

// ───────────── List page ─────────────

function RecipesBar({ onInstall }) {
  const [recipes, setRecipes] = useState([]);
  useEffect(() => {
    fetch(`${API}/recipe-templates`, { headers: auth() })
      .then(r => r.json()).then(j => setRecipes(j.data || []));
  }, []);
  if (recipes.length === 0) return null;
  return (
    <div className="mb-8" data-testid="recipes-section">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="text-amber-500" size={18} />
        <h2 className="font-semibold text-slate-900 dark:text-white">One-click recipes</h2>
        <span className="text-xs text-slate-500">Install a ready-made automation in 2 taps</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
        {recipes.map((r) => {
          const meta = CATEGORY_META[r.category] || CATEGORY_META.Sales;
          const Icon = meta.icon;
          return (
            <button
              key={r.id}
              onClick={() => onInstall(r.id)}
              data-testid={`install-recipe-${r.id}`}
              className="text-left p-3 rounded-lg border-2 border-dashed hover:border-solid hover:shadow-md transition"
              style={{ borderColor: meta.color, background: meta.bg }}
            >
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide" style={{ color: meta.color }}>
                <Icon size={12} /> {r.category}
              </div>
              <div className="mt-1.5 font-medium text-sm text-slate-900 line-clamp-2">{r.title}</div>
              <div className="mt-1 text-xs text-slate-600 line-clamp-2">{r.description}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function FlowListPanel({ onOpen, onNew }) {
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(API, { headers: auth() });
      const j = await r.json();
      setFlows(j.data || []);
    } catch (e) { toast.error("Failed to load flows"); }
    setLoading(false);
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const installRecipe = async (recipeId) => {
    const r = await fetch(`${API}/install-recipe/${recipeId}`, { method: "POST", headers: auth() });
    const j = await r.json();
    if (j.success) {
      toast.success(`Installed: ${j.data.name}`);
      reload();
      onOpen(j.data.flow_id);
    } else toast.error("Install failed");
  };

  const toggle = async (f) => {
    if (f.is_active && f.is_default && !confirm(`"${f.name}" is a default flow. Pausing it means orders will not be auto-checked for risk anymore. Pause it?`)) return;
    const url = `${API}/${f.flow_id}/${f.is_active ? "deactivate" : "activate"}`;
    const r = await fetch(url, { method: "PUT", headers: auth() });
    if (!r.ok) {
      const j = await r.json().catch(() => ({}));
      toast.error(j.error?.message || j.detail || "Toggle failed");
      return;
    }
    toast.success(`Flow ${f.is_active ? "paused" : "activated"}`);
    reload();
  };
  const duplicate = async (f) => {
    await fetch(`${API}/${f.flow_id}/duplicate`, { method: "POST", headers: auth() });
    toast.success("Flow duplicated");
    reload();
  };
  const del = async (f) => {
    if (!confirm(`Delete flow "${f.name}"?`)) return;
    await fetch(`${API}/${f.flow_id}`, { method: "DELETE", headers: auth() });
    toast.success("Flow deleted");
    reload();
  };
  const [tutorialOpen, setTutorialOpen] = useState(false);

  return (
    <div className="p-6" data-testid="flows-list-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white">Flow Builder</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Build automations for any dashboard — sales, field force, operations, HR, finance, intelligence, scheduled. No code needed.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            onClick={() => setTutorialOpen(true)}
            className="bg-white dark:bg-slate-700 border-2 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-600 font-medium"
            data-testid="open-tutorial-btn"
          >
            <HelpCircle size={14} className="mr-1.5" /> Tutorial
          </Button>
          <Button onClick={onNew} data-testid="new-flow-btn" className="bg-[#8B1A1A] hover:bg-[#6f1414] text-white">
            <Plus size={16} className="mr-1" /> New flow
          </Button>
        </div>
      </div>
      <FlowTutorialDialog open={tutorialOpen} onClose={() => setTutorialOpen(false)} />

      <RecipesBar onInstall={installRecipe} />

      <div className="flex items-center gap-2 mb-3">
        <h2 className="font-semibold text-slate-900 dark:text-white">Your flows</h2>
        <span className="text-xs text-slate-500">{flows.length} total</span>
      </div>
      {loading ? (
        <div className="text-slate-500">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {flows.map((f) => (
            <Card key={f.flow_id} className="p-4 hover:shadow-md transition cursor-pointer dark:bg-slate-800 overflow-hidden" data-testid={`flow-card-${f.flow_id}`}>
              <div className="min-w-0" onClick={() => onOpen(f.flow_id)}>
                <div className="flex items-center gap-2 min-w-0">
                  <h3 className="font-semibold text-slate-900 dark:text-white truncate flex-1 min-w-0">{f.name}</h3>
                  {f.is_default && <Badge variant="outline" className="text-[10px] shrink-0">Default</Badge>}
                </div>
                <p className="text-xs text-slate-500 mt-1 line-clamp-2">{f.description}</p>
                <div className="mt-3 flex items-center gap-2 text-xs text-slate-500">
                  <Zap size={12} /> <span className="truncate">{f.trigger?.event}</span>
                </div>
                <div className="mt-2 text-xs text-slate-400">
                  {f.run_count || 0} runs · {f.last_run_at ? new Date(f.last_run_at).toLocaleString() : "never run"}
                </div>
              </div>
              <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => onOpen(f.flow_id)} data-testid={`open-${f.flow_id}`}>Open</Button>
                  <Button size="sm" variant="ghost" onClick={() => duplicate(f)} title="Duplicate"><Copy size={14} /></Button>
                  {!f.is_default && (
                    <Button size="sm" variant="ghost" onClick={() => del(f)} title="Delete"><Trash2 size={14} className="text-red-500" /></Button>
                  )}
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); toggle(f); }}
                  title={f.is_active ? "Pause this flow" : "Activate this flow"}
                  data-testid={`flow-toggle-${f.flow_id}`}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border transition shrink-0 ${
                    f.is_active
                      ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800"
                      : "bg-slate-100 text-slate-600 border-slate-300 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:border-slate-600"
                  }`}
                >
                  {f.is_active ? (
                    <>
                      <ToggleRight size={14} /> Active
                    </>
                  ) : (
                    <>
                      <ToggleLeft size={14} /> Paused
                    </>
                  )}
                </button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ───────────── Chip Input ─────────────



// ───────────── Page ─────────────

export default function FlowsPage() {
  const [activeFlowId, setActiveFlowId] = useState(null);
  const [showNew, setShowNew] = useState(false);

  if (activeFlowId) return (
    <FlowErrorBoundary>
      <FlowCanvas flowId={activeFlowId} onBack={() => setActiveFlowId(null)} />
    </FlowErrorBoundary>
  );

  return (
    <FlowErrorBoundary>
      <FlowListPanel onOpen={(id) => setActiveFlowId(id)} onNew={() => setShowNew(true)} />
      <NewFlowWizard
        open={showNew}
        onClose={() => setShowNew(false)}
        onCreated={(id) => { setShowNew(false); setActiveFlowId(id); }}
      />
    </FlowErrorBoundary>
  );
}
